import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import ExpenseDistributionViews from './ExpenseDistributionsView';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { useApplicationSettingsSelector, ApplicationSettingsSelectors, TaxMode } from '../store/modules/applicationSettings';
import SalesItemDistributionViews from './SalesItemDistributionsView';
import { red, yellow, green } from '@material-ui/core/colors';
import AttachmentViews from './AttachmentsView';

interface ISalesReceiptViewProps {
    fileIndex: number;
    salesReceiptIndex: number;
    salesReceiptPosition: number;
    importSuccess: boolean;
    importError: boolean;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    financialError: {
        background: `${theme.palette.warning.dark} !important`,
        color: `${theme.palette.common.white} !important`
    },
    warning: {
        '& td': {
            background: yellow[100]
        }
    },
    errorRow: {
        '& td': {
            background: red[100]
        }
    },
    successRow: {
        '& td': {
            background: green[100]
        }
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0),
        paddingRight: `${theme.spacing(0)} !important`
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const SalesReceiptView: React.FunctionComponent<ISalesReceiptViewProps> = ({
    fileIndex,
    salesReceiptIndex,
    salesReceiptPosition,
    importError,
    importSuccess
}) => {
    const salesReceipt = useIifImportSelector(state => IifImportSelectors.selectSalesReceipt(state, salesReceiptIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!salesReceipt) {
        return null;
    }

    return (
        <>
            <TableRow className={classNames({ [classes.warning]: salesReceipt.distributionErrors || salesReceipt.attachmentErrors || !salesReceipt.attachmentsDownloaded || salesReceipt.financialYearError }, { [classes.successRow]: importSuccess }, { [classes.errorRow]: importError })}>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{salesReceiptPosition}</TableCell>
                <TableCell className={classNames({ [classes.financialError]: salesReceipt.financialYearError })}>{salesReceipt.transactionDate ? DateTime.fromISO(salesReceipt.transactionDate).toFormat('dd/MM/yyyy') : null}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.accountError })}>{salesReceipt.depositToAccount?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.customerError })}>{salesReceipt.customer?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.classError })}>{salesReceipt.class?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.paymentMethodError })}>{salesReceipt.paymentMethod?.value}</TableCell>
                <TableCell>{salesReceipt.docNumber}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.currencyError })}>{salesReceipt.currency?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: salesReceipt.amountError })} align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(salesReceipt.distributionAmount ?? 0) :
                            AudCurrencyFormatter.format(salesReceipt.distributionTotal ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={9} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <div className={classes.padding}>
                                    {
                                        salesReceipt.billingAddress && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={3} lg={2}>
                                                    <Typography>
                                                        <strong>
                                                            Address
                                                </strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    {
                                                        salesReceipt.billingAddress.line1 && (
                                                            <Typography>
                                                                {salesReceipt.billingAddress.line1}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        salesReceipt.billingAddress.line2 && (
                                                            <Typography>
                                                                {salesReceipt.billingAddress.line2}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        salesReceipt.billingAddress.line3 && (
                                                            <Typography>
                                                                {salesReceipt.billingAddress.line3}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        salesReceipt.billingAddress.line4 && (
                                                            <Typography>
                                                                {salesReceipt.billingAddress.line4}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        salesReceipt.billingAddress.line5 && (
                                                            <Typography>
                                                                {salesReceipt.billingAddress.line5}
                                                            </Typography>
                                                        )
                                                    }
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                </div>
                                <SalesItemDistributionViews
                                    distributionView={salesReceipt.distributionView}
                                    amount={salesReceipt.distributionAmount ?? 0}
                                    tax={salesReceipt.distributionTax ?? 0}
                                    total={salesReceipt.distributionTotal ?? 0}
                                />
                                <AttachmentViews
                                    fileIndex={fileIndex}
                                    type="salesReceipts"
                                    typeIndex={salesReceiptIndex}
                                    attachmentView={salesReceipt.attachmentView}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default SalesReceiptView;


// WEBPACK FOOTER //
// ./src/components/SalesReceiptView.tsx